// src/pages/admin/BankFeedsModal.jsx
import { useRef } from "react";
import { Loading, Modal } from "@common";

// Opens the modal; shown on the To post tab and when no clinic is selected.
export function BankFeedsButton({ onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="h-[30px] whitespace-nowrap rounded-md border border-palette-slate-divider bg-white px-3 text-[12.5px] font-semibold text-palette-emerald-hover shadow-sm transition-colors hover:bg-palette-emerald-soft"
    >
      Bank feeds
    </button>
  );
}

const fmtMonthDay = (iso) =>
  new Date(`${iso}T00:00:00`).toLocaleDateString("en-CA", { month: "short", day: "numeric" });
const ROW_GRID = "grid grid-cols-[minmax(0,1.6fr)_104px_84px_60px_118px] items-center gap-3 px-5";

const TONES = {
  ok: { text: "text-palette-emerald-hover", dot: "bg-palette-emerald-hover" },
  warn: { text: "text-palette-amber-primary", dot: "bg-palette-amber-primary" },
};

function FeedRow({ feed, onSync, onUpload, uploading }) {
  const fileRef = useRef(null);
  const isNetsuite = feed.feed === "netsuite";
  // RBC is both: NetSuite's feed, plus CSVs for days NetSuite hasn't imported.
  const upload = !!feed.uploadable;
  const action = upload ? onUpload : isNetsuite ? onSync : null;
  const t = feed.needsCsv ? TONES.warn : TONES.ok;

  return (
    <div className={`${ROW_GRID} border-b border-palette-slate-divider py-3`}>
      <div className="flex min-w-0 items-center gap-3">
        <span
          className={`inline-flex h-[34px] w-[34px] flex-none items-center justify-center rounded-lg border text-[10.5px] font-bold tracking-wider ${
            isNetsuite
              ? "border-palette-emerald-primary bg-palette-emerald-primary text-white"
              : "border-palette-slate-divider bg-palette-slate-muted text-palette-slate-heading"
          }`}
        >
          {feed.code}
        </span>
        <div className="flex min-w-0 flex-col gap-0.5">
          <span className="truncate text-sm font-semibold text-palette-slate-primary">
            {feed.name}
          </span>
          <span className="truncate text-xs text-palette-slate-placeholder">{feed.source}</span>
        </div>
      </div>
      <span
        className={`inline-flex items-center gap-1.5 whitespace-nowrap text-[12.5px] font-semibold ${t.text}`}
      >
        <span className={`h-[7px] w-[7px] flex-none rounded-full ${t.dot}`} />
        {feed.needsCsv ? "Needs CSV" : isNetsuite ? "Live" : "Current"}
      </span>
      <span className="whitespace-nowrap text-[13px] tabular-nums text-palette-slate-heading">
        {feed.dataUpTo ? fmtMonthDay(feed.dataUpTo) : "—"}
      </span>
      <span className="whitespace-nowrap text-right text-[13px] tabular-nums text-palette-slate-heading">
        {feed.total ?? "—"}
      </span>
      <div className="flex justify-end">
        <button
          type="button"
          disabled={!action || uploading}
          title={action ? undefined : "Not available yet"}
          onClick={upload ? () => fileRef.current?.click() : onSync}
          className={`inline-flex h-[30px] items-center justify-center whitespace-nowrap rounded-lg px-3 text-[13px] font-medium text-palette-emerald-hover transition-colors hover:bg-palette-emerald-soft disabled:cursor-not-allowed disabled:opacity-50 ${
            feed.needsCsv || isNetsuite
              ? "border border-palette-slate-divider bg-white shadow-sm"
              : "border border-transparent"
          }`}
        >
          {!upload && isNetsuite ? "Sync now" : uploading ? "Reading…" : "Upload CSV"}
        </button>
        {upload && (
          <input
            ref={fileRef}
            type="file"
            accept=".csv,text/csv"
            className="hidden"
            onChange={(e) => {
              const file = e.target.files?.[0];
              e.target.value = "";
              if (file) onUpload?.(feed, file);
            }}
          />
        )}
      </div>
    </div>
  );
}

const fmtCad = (n) =>
  Number(n || 0).toLocaleString("en-CA", { style: "currency", currency: "CAD" });

// Line status from the backend preview. Only "new" lines are ever posted.
const LINE_STATUS = {
  new: { row: "text-palette-slate-heading", title: undefined },
  loaded: {
    row: "text-palette-slate-placeholder line-through",
    title: "Already loaded — will not be posted again",
  },
  duplicate: {
    row: "bg-palette-amber-soft text-palette-amber-primary line-through",
    title:
      "Same date and amount already loaded for this account (from NetSuite, or with a " +
      "different description) — " +
      "treated as a duplicate and not posted",
  },
  repeated: {
    row: "bg-palette-amber-soft text-palette-amber-primary line-through",
    title: "The same line appears earlier in this file — posted once only",
  },
};

// What an uploaded CSV would add, before anything is written. Lines already
// loaded, and likely duplicates of loaded lines, are struck out and not posted.
function UploadPreview({ preview, onPost, onCancel, posting }) {
  const { file, data } = preview;
  // A Desjardins file covers several accounts: list each, and tag every line.
  const multi = data.accounts?.length > 1;
  return (
    <div className="flex flex-col">
      <div className="border-y border-palette-slate-divider bg-palette-slate-soft px-5 py-3 text-[13px] text-palette-slate-secondary">
        {multi ? (
          <ul className="mb-1">
            {data.accounts.map((a) => (
              <li key={a.id}>
                <span className="font-semibold text-palette-slate-primary">{a.label}</span>
                {` · ${a.inserted} new`}
                {a.skipped ? ` · ${a.skipped} already loaded` : ""}
                {a.duplicates ? ` · ${a.duplicates} duplicate` : ""}
              </li>
            ))}
          </ul>
        ) : (
          <div className="font-semibold text-palette-slate-primary">{data.bank_account.label}</div>
        )}
        <div>
          {file.name} · {data.date_from} – {data.date_to} · <b>{data.inserted}</b> new
          {data.skipped ? ` · ${data.skipped} already loaded` : ""}
          {data.duplicates ? (
            <span className="font-semibold text-palette-amber-primary">
              {` · ${data.duplicates} likely ${data.duplicates === 1 ? "duplicate" : "duplicates"}`}
            </span>
          ) : null}
        </div>
        {data.ignored?.length > 0 && (
          <div className="mt-1 text-xs text-palette-slate-placeholder">
            Not imported:{" "}
            {data.ignored.map((g) => `${g.folio} ${g.type} (${g.rows}, ${g.reason})`).join(" · ")}
          </div>
        )}
      </div>
      <div className="max-h-[50vh] overflow-y-auto">
        <table className="w-full text-[13px]">
          <thead className="sticky top-0 bg-white text-[11px] font-semibold uppercase tracking-wider text-palette-slate-placeholder">
            <tr className="border-b border-palette-slate-divider">
              <th className="px-5 py-2 text-left">Date</th>
              {multi && <th className="py-2 pr-3 text-left">Account</th>}
              <th className="py-2 text-left">Description</th>
              <th className="px-5 py-2 text-right">Amount</th>
            </tr>
          </thead>
          <tbody>
            {data.lines.map((l, i) => (
              <tr
                key={i}
                title={(LINE_STATUS[l.status] ?? LINE_STATUS.loaded).title}
                className={`border-b border-palette-slate-muted ${
                  (LINE_STATUS[l.status] ?? LINE_STATUS.loaded).row
                }`}
              >
                <td className="whitespace-nowrap px-5 py-1.5 tabular-nums">{l.posted_date}</td>
                {multi && (
                  <td className="whitespace-nowrap py-1.5 pr-3">
                    {l.account.match(/DES:\s*(.*)$/)?.[1] ?? l.account}
                  </td>
                )}
                <td className="py-1.5">{l.description}</td>
                <td
                  className={`whitespace-nowrap px-5 py-1.5 text-right tabular-nums ${
                    l.new && Number(l.amount) > 0 ? "text-palette-emerald-hover" : ""
                  }`}
                >
                  {fmtCad(l.amount)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="flex items-center justify-end gap-2 px-5 py-3">
        <button
          type="button"
          onClick={onCancel}
          disabled={posting}
          className="h-9 rounded-lg px-3 text-[13px] font-semibold text-palette-slate-secondary transition-colors hover:bg-palette-slate-muted disabled:opacity-50"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={onPost}
          disabled={posting || data.inserted === 0}
          className="h-9 rounded-lg bg-palette-emerald-primary px-4 text-[13px] font-semibold text-white transition-colors hover:bg-palette-emerald-hover disabled:cursor-not-allowed disabled:opacity-50"
        >
          {posting
            ? "Posting…"
            : data.inserted === 0
              ? "Nothing new to post"
              : `Post ${data.inserted} ${data.inserted === 1 ? "line" : "lines"}`}
        </button>
      </div>
    </div>
  );
}

/**
 * BankFeedsModal — every bank our clinics use (whatever clinic is selected), how far its lines run,
 * and the action that refreshes it (Sync now for NetSuite, Upload CSV for the
 * rest). Actions render disabled until a handler is passed.
 *
 *   feeds: buildBankFeeds() output, with `dataUpTo` and `needsCsv`.
 */
export default function BankFeedsModal({
  isOpen,
  onClose,
  feeds = [],
  loading = false,
  onSync,
  // onUpload(feed, file); only feeds marked `uploadable` offer it.
  onUpload,
  uploadingType = null,
  // { tone: "ok" | "error", text } from the last upload.
  uploadNote = null,
  // { feed, file, data } — a CSV read but not yet posted. While set, the
  // modal shows its lines and a Post button instead of the bank list.
  preview = null,
  onPost,
  onCancelPreview,
  posting = false,
}) {
  const stale = feeds.filter((f) => f.needsCsv).map((f) => f.name);

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={preview ? `Review ${preview.feed.name} CSV` : "Bank feeds"}
      size="lg"
    >
      {preview ? (
        <div className="-mx-6 -mb-6">
          <UploadPreview
            preview={preview}
            onPost={onPost}
            onCancel={onCancelPreview}
            posting={posting}
          />
        </div>
      ) : (
        <div className="-mx-6 -mb-6 flex flex-col">
          {stale.length > 0 && (
            <div className="border-y border-palette-slate-divider bg-palette-slate-soft px-5 py-3 text-[13px] text-palette-slate-secondary">
              {stale.length} {stale.length === 1 ? "bank needs" : "banks need"} a fresh CSV before
              every line can be matched: {stale.join(", ")}.
            </div>
          )}
          <div
            className={`${ROW_GRID} border-b border-palette-slate-divider py-2 text-[11px] font-semibold uppercase tracking-wider text-palette-slate-placeholder`}
          >
            <span>Bank</span>
            <span>Status</span>
            <span>Data up to</span>
            <span className="text-right">Lines</span>
            <span />
          </div>
          {loading ? (
            <Loading />
          ) : feeds.length === 0 ? (
            <p className="px-5 py-4 text-sm text-palette-slate-secondary">
              No RBC, BMO, Desjardins or Meridian accounts.
            </p>
          ) : (
            feeds.map((f) => (
              <FeedRow
                key={f.type}
                feed={f}
                onSync={onSync}
                onUpload={onUpload}
                uploading={uploadingType === f.type}
              />
            ))
          )}
          {uploadNote && (
            <p
              className={`border-b border-palette-slate-divider px-5 py-3 text-[13px] ${
                uploadNote.tone === "error"
                  ? "text-palette-rose-heading"
                  : "text-palette-emerald-hover"
              }`}
            >
              {uploadNote.text}
            </p>
          )}
          <p className="px-5 py-3 text-xs text-palette-slate-placeholder">
            “Data up to” is the latest deposit date we have for that bank — anything after it can’t
            be matched until a newer CSV is uploaded. RBC refreshes from NetSuite nightly.
          </p>
        </div>
      )}
    </Modal>
  );
}
